import {makeTrackEvent} from './makeTrackEvent';

export type ObservabilityAction =
  | 'Click run issues button'
  | 'Expand grouped issue'
  | 'Click issue tag'
  | 'Click issue alert';

type ObservabilityEventBase = {
  action: ObservabilityAction;
  entityName: string;
  projectName: string;
};

export const trackObservabilityRunIssuesButton = makeTrackEvent<
  ObservabilityEventBase & {
    action: 'Click run issues button';
  },
  {
    _description: 'Tracks a user opening the issues drawer';
    _location: 'Workspace UI';
    _motivation: 'Track usage of observability';
    action: {
      description: 'User clicked the run issues button';
      exampleValues: ['Click run issues button'];
    };
    entityName: {
      description: 'Entity/organization name';
      exampleValues: ['my-org', 'user-name'];
    };
    projectName: {
      description: 'Project name';
      exampleValues: ['my-project', 'experiments'];
    };
  }
>('Observability Run Issues Button Clicked');

export const trackObservabilityExpandGroupedIssue = makeTrackEvent<
  ObservabilityEventBase & {
    action: 'Expand grouped issue';
    alertName: string;
    alertsInGroup: number;
  },
  {
    _description: 'Tracks a user expanding a grouped issues card';
    _location: 'Issues Drawer';
    _motivation: 'Track what issues users are interested in seeing in detail';
    action: {
      description: 'User expanded a grouped issue to see details';
      exampleValues: ['Expand grouped issue'];
    };
    alertName: {
      description: 'Name of the alert associated with the issue';
      exampleValues: [
        'Missing Logged Metric',
        'Validation Error',
        'Runtime Exception',
      ];
    };
    alertsInGroup: {
      description: 'Number of alerts in the expanded group';
      exampleValues: [1, 5, 10];
    };
    entityName: {
      description: 'Entity/organization name';
      exampleValues: ['my-org', 'user-name'];
    };
    projectName: {
      description: 'Project name';
      exampleValues: ['my-project', 'experiments'];
    };
  }
>('Observability Grouped Issue Expanded');

export const trackObservabilityTagClick = makeTrackEvent<
  ObservabilityEventBase & {
    action: 'Click issue tag';
    issuesCount: number;
    tagType: 'alert' | 'success' | 'error';
  },
  {
    _description: 'Tracks users clicking a tag in the issues column';
    _location: 'The issues column in the run selector or workspace table';
    _motivation: 'See how often users click into the issues drawer';
    action: {
      description: 'User clicked on an issue tag';
      exampleValues: ['Click issue tag'];
    };
    issuesCount: {
      description: 'Number of issues behind the tag';
      exampleValues: [1, 5, 10];
    };
    tagType: {
      description: 'Tag type indicating issues state';
      exampleValues: ['alert', 'success', 'error'];
    };
    entityName: {
      description: 'Entity/organization name';
      exampleValues: ['my-org', 'user-name'];
    };
    projectName: {
      description: 'Project name';
      exampleValues: ['my-project', 'experiments'];
    };
  }
>('Observability Issue Tag Clicked');

export const trackObservabilityIssueAlertClick = makeTrackEvent<
  ObservabilityEventBase & {
    action: 'Click issue alert';
  },
  {
    _description: 'Tracks a user clicking an alert icon in a chart';
    _location: 'A runs line plot';
    _motivation: 'Track how often users explore issues from the chart UI';
    action: {
      description: 'User clicked on an issue alert in the layout';
      exampleValues: ['Click issue alert'];
    };
    entityName: {
      description: 'Entity/organization name';
      exampleValues: ['my-org', 'user-name'];
    };
    projectName: {
      description: 'Project name';
      exampleValues: ['my-project', 'experiments'];
    };
  }
>('Observability Issue Alert Clicked');
